import {
  type Instant,
  type LocalDate,
  fromApiMoney,
  type Money,
  formatLocalDate,
  parseInstant,
  parseLocalDate,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export type PrepaidRecognitionStatus = Schemas['PrepaidRecognitionResponse']['status']

export interface PrepaidRecognition {
  readonly id: string
  readonly originEntryId: string
  readonly assetAccountId: string
  readonly expenseAccountId: string
  readonly recognitionDate: LocalDate
  readonly amount: Money
  readonly status: PrepaidRecognitionStatus
  readonly recognitionEntryId: string | undefined
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

/**
 * The server's own figures - never derive one from the other three on this client. If they
 * ever stop summing to `totalAmount`, that is a fact worth surfacing, not one to paper over.
 */
export interface PrepaidSchedule {
  readonly originEntryId: string
  readonly totalAmount: Money
  readonly recognizedAmount: Money
  readonly pendingAmount: Money
  readonly cancelledAmount: Money
  readonly recognitions: readonly PrepaidRecognition[]
}

export interface SchedulePrepaidRecognitionInput {
  readonly originEntryId: string
  readonly expenseAccountId: string
  readonly periods: number
  readonly startDate: LocalDate
}

export const prepaidRecognitionKeys = {
  schedule: (ledgerId: string, originEntryId: string): CacheKey =>
    ledgerKey(ledgerId, 'prepaid-recognition-schedules', originEntryId),
  /** Scheduling a prepaid recognition only ever affects its own origin entry's schedule. */
  invalidatedByWrite: (ledgerId: string, originEntryId: string): Invalidations => [
    ledgerKey(ledgerId, 'prepaid-recognition-schedules', originEntryId),
  ],
} as const

export interface PrepaidRecognitionsRepository {
  getSchedule(ledgerId: string, originEntryId: string, signal?: AbortSignal): Promise<PrepaidSchedule>
  scheduleRecognition(ledgerId: string, input: SchedulePrepaidRecognitionInput): Promise<PrepaidSchedule>
  getRecognition(ledgerId: string, recognitionId: string, signal?: AbortSignal): Promise<PrepaidRecognition>
}

export function createPrepaidRecognitionsRepository(client: TalerClient): PrepaidRecognitionsRepository {
  const schedulesBase = (ledgerId: string) =>
    `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/prepaid-recognition-schedules`
  const scheduleOne = (ledgerId: string, originEntryId: string) =>
    `${schedulesBase(ledgerId)}/${encodeURIComponent(originEntryId)}`
  const recognitionOne = (ledgerId: string, recognitionId: string) =>
    `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/prepaid-recognitions/${encodeURIComponent(recognitionId)}`

  return {
    async getSchedule(ledgerId, originEntryId, signal) {
      return toPrepaidSchedule(
        await client.call<Schemas['PrepaidScheduleResponse']>({
          method: 'GET',
          path: scheduleOne(ledgerId, originEntryId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async scheduleRecognition(ledgerId, input) {
      return toPrepaidSchedule(
        await client.call<Schemas['PrepaidScheduleResponse'], Schemas['SchedulePrepaidRecognitionRequest']>({
          method: 'POST',
          path: schedulesBase(ledgerId),
          body: {
            originEntryId: input.originEntryId,
            expenseAccountId: input.expenseAccountId,
            periods: input.periods,
            startDate: formatLocalDate(input.startDate),
          },
        }),
      )
    },

    async getRecognition(ledgerId, recognitionId, signal) {
      return toPrepaidRecognition(
        await client.call<Schemas['PrepaidRecognitionResponse']>({
          method: 'GET',
          path: recognitionOne(ledgerId, recognitionId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },
  }
}

function toPrepaidRecognition(response: Schemas['PrepaidRecognitionResponse']): PrepaidRecognition {
  return {
    id: response.id,
    originEntryId: response.originEntryId,
    assetAccountId: response.assetAccountId,
    expenseAccountId: response.expenseAccountId,
    recognitionDate: parseLocalDate(response.recognitionDate),
    amount: fromApiMoney(response.amount),
    status: response.status,
    recognitionEntryId: response.recognitionEntryId,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

function toPrepaidSchedule(response: Schemas['PrepaidScheduleResponse']): PrepaidSchedule {
  return {
    originEntryId: response.originEntryId,
    totalAmount: fromApiMoney(response.totalAmount),
    recognizedAmount: fromApiMoney(response.recognizedAmount),
    pendingAmount: fromApiMoney(response.pendingAmount),
    cancelledAmount: fromApiMoney(response.cancelledAmount),
    recognitions: response.recognitions.map(toPrepaidRecognition),
  }
}
